import React from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCircleCheck, LuClock, LuInfo, LuUsers, LuUserX } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import useSmoText from '../smoText';
import { PanelTile } from './PanelTile';
import { TEXT_TONES } from './tones';

const CELL = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-tabular-nums';
const COLUMNS = ['worker', 'role', 'jobs', 'done', 'open', 'overdue', 'donePct', 'activeDays', 'lastDay', 'actions'];

/** Body of "Viziti Olmayan Çalışanlar": their calendar jobs in the period. */
const NoVisitWorkers = ({ report, onOpenJobs }) => {
  const { label, locale } = useSmoText();
  const { rows, totals } = report;

  const formatDay = (value) => {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="tw-space-y-4">
      <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-sky-500" />
        {label('noVisit.help')}
      </p>

      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        <PanelTile icon={LuUsers} value={totals.workers} title={label('noVisit.tiles.workers')} />
        <PanelTile icon={LuBriefcase} value={totals.jobs} title={label('noVisit.tiles.jobs')} />
        <PanelTile icon={LuCircleCheck} iconClass="tw-text-emerald-500" value={totals.donePct === null ? '-' : `${totals.donePct}%`} title={label('noVisit.tiles.done')} />
        <PanelTile icon={LuClock} value={totals.overdue} title={label('noVisit.tiles.overdue')} alert={totals.overdue > 0} />
        <PanelTile icon={LuUserX} value={totals.withoutJobs} of={totals.workers} title={label('noVisit.tiles.noRecord')} alert={totals.withoutJobs > 0} />
      </div>

      <div className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white glass-scrollbar dark:tw-border-white/10 dark:tw-bg-[#1f232b]">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
            <tr>
              {COLUMNS.map((key, index) => (
                <th key={key} scope="col" className={`tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400 ${index === 0 ? 'tw-sticky tw-left-0 tw-bg-slate-50 dark:tw-bg-[#262a32]' : ''}`}>
                  {label(`noVisit.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="tw-sticky tw-left-0 tw-whitespace-nowrap tw-bg-white tw-px-3 tw-py-2.5 tw-font-semibold tw-text-slate-900 dark:tw-bg-[#1f232b] dark:tw-text-white">{row.name}</td>
                <td className={CELL}>{row.roles.join(', ') || '-'}</td>
                <td className={`${CELL} tw-font-semibold`}>{row.total}</td>
                <td className={CELL}>{row.done}</td>
                <td className={CELL}>{row.open}</td>
                <td className={`${CELL} ${row.overdue > 0 ? `tw-font-bold ${TEXT_TONES.bad}` : ''}`}>{row.overdue}</td>
                <td className={`${CELL} ${row.donePct === null ? '' : `tw-font-semibold ${row.donePct < 80 ? TEXT_TONES.bad : TEXT_TONES.ok}`}`}>{row.donePct === null ? '-' : `${row.donePct}%`}</td>
                <td className={CELL}>{row.activeDays}</td>
                <td className={CELL}>{row.lastDay ? formatDay(row.lastDay) : <Badge tone="warning">{label('noVisit.noRecord')}</Badge>}</td>
                <td className={CELL}>
                  <Button size="sm" disabled={row.total === 0} onClick={() => onOpenJobs(row.worker)} title={label('noVisit.viewJobsHint')}>
                    <LuBriefcase aria-hidden="true" className="tw-h-4 tw-w-4" />
                    {label('noVisit.viewJobs')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

NoVisitWorkers.propTypes = {
  report: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    totals: PropTypes.object.isRequired,
  }).isRequired,
  onOpenJobs: PropTypes.func.isRequired,
};

export default NoVisitWorkers;
